import React, {useContext, useEffect, useState} from 'react';
import classNames from 'classnames/bind';
import mapboxgl from 'mapbox-gl';

import ExpandableMapControl from 'core/ui/mapping/expandable-map-control';
import {emptyGeoJSON} from "core/ui/mapping/map";
import map_pin_img from 'images/map-pin-solid.svg';
import map_pin_cur from 'images/map-pin-solid.cur';
import useDraggableRadius from "hooks/useDraggableRadius";
import {pulseLayer} from "../core/ui/mapping/map";

import SessionContext from 'SessionContext'

export default function MapAdHoc(props) {
  const session = useContext(SessionContext)
  const {
    disable_draggable_radius,
    redraw_location_radius_filters,
    enableDraggableRadiusForLayer
  } = useDraggableRadius(props.map)

  const [state, setState] = useState({
    dragging: false
  })

  const [cancelPulseLayer, setCancelPulseLayer] = useState(undefined)

  const [visibleGeoBoundaryLayer, setVisibleGeoBoundaryLayer] = useState('');

  useEffect(() => {
    initializeMapLayers();
    redraw_location_radius_filters();

    return () => {
      cancelPulseLayer && cancelPulseLayer()
      setCancelPulseLayer(undefined)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    drawAdHocPoints()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore.adhoc_points, session.crossfilterStore.locations])

  useEffect(() => {
    redraw_location_radius_filters()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.crossfilterStore.filters])

  function initializeMapLayers() {
    const map = props.map;
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      anchor: 'top'
    });

    map.addNamedImage('map-pin', map_pin_img, 14, 25);
    if (!map.getSource('ad-hoc-points')) {
      map.addSource('ad-hoc-points', emptyGeoJSON());
    }

    if (!map.getLayer('ad-hoc-points-established')) {
      map.addLayer({
        "id": "ad-hoc-points-established",
        "source": "ad-hoc-points",
        "type": "symbol",
        "filter": ['has', 'series'],
        'layout': {
          'icon-image': 'map-pin',
          'icon-anchor': 'bottom',
        }
      });
    }
    if (!map.getLayer('ad-hoc-points-establishing')) {
      map.addLayer({
        "id": "ad-hoc-points-establishing",
        "source": "ad-hoc-points",
        "type": "symbol",
        "filter": ['!', ['has', 'series']],
        'layout': {
          'icon-image': 'map-pin',
          'icon-anchor': 'bottom',
          'text-field': 'Establishing Search Radius',
          'text-size': 11,
          'text-anchor': 'top',
        },
        'paint': {
          "text-color": "#333",
          "text-halo-color": "rgba(242,243,240,.3)",
          "text-halo-width": 2,
          "text-halo-blur": 1
        }
      });
    }
    bindLocationPopupToLayer(map, 'ad-hoc-points-established', popup);
    bindLocationPopupToLayer(map, 'ad-hoc-points-establishing', popup);
    enableDraggableRadiusForLayer('ad-hoc-points-established');
  }

  function bindLocationPopupToLayer(map, layer, popup){
    map.on('mouseenter', layer, function(e) {
      var coordinates = e.features[0].geometry.coordinates.slice();

      // Populate the popup and set its coordinates
      // based on the feature found.
      popup.setLngLat(coordinates)
          .setHTML(e.features[0].properties.name)
          .addTo(map);
    });

    map.on('mouseleave', layer, function() {
      popup.remove();
    });

  }

  function drawAdHocPoints(){
    const map = props.map;
    map.getSource('ad-hoc-points').setData({
      "type": "FeatureCollection",
      "features": (session.crossfilterStore.adhoc_points || []).map((point) => ({
        "type": "Feature",
        'properties': point,
        "geometry": {
          "type": "Point",
          "coordinates": [point.lng, point.lat]
        }
      }))
    });

    if(session.crossfilterStore.adhoc_points.some(pt => !pt.series) && !cancelPulseLayer){
      setCancelPulseLayer(pulseLayer(map, 'ad-hoc-points-establishing', 2000));
    } else {
      cancelPulseLayer && cancelPulseLayer();
      setCancelPulseLayer(undefined);
    }
  }

  function onExpand(){
  }

  function startDragging(dropevent) {
    disable_draggable_radius();
    const map = props.map;
    map.on(dropevent, dropPin);
    map.getCanvas().style.cursor = `url('${map_pin_cur}'), crosshair`;
    setState({ dragging: true });
  }

  function stopDragging(){
    const map = props.map;
    map.off('click', dropPin);
    map.off('mouseup', dropPin);
    map.getCanvas().style.cursor = '';
    setState({dragging: false});
    disable_draggable_radius(false);
    props.onMapUpdate && props.onMapUpdate();
  }

  function dropPin(ev) {
    stopDragging();
    unHideVisibleGeoBoundaryLayers();
    session.crossfilterStore.add_adhoc_point(ev.lngLat);
    props.onMapUpdate && props.onMapUpdate();
  }

  function onClickPin(ev) {
    hideVisibleGeoBoundaryLayers();
    state.dragging
      ? stopDragging()
      : startDragging('click');
  }

  // Temporarily hide geo-boundary layers so pin drop doens't trigger a click
  function hideVisibleGeoBoundaryLayers() {
    const map = props.map;
    const geoLayers = ['states', 'counties', 'zipcodes', 'congressional_districts', 'cbsa'];
    let gbLayerID = '';
    geoLayers.forEach((gbl) => { 
      const vlayer = map.getLayoutProperty(gbl, 'visibility');
      if (vlayer === 'visible') {
        setVisibleGeoBoundaryLayer(gbl);
        gbLayerID = gbl;
      }
    });

    gbLayerID && map.setLayoutProperty(gbLayerID, 'visibility', 'none');
  }

  // after pin drop, make previous geo boundary visible again
  function unHideVisibleGeoBoundaryLayers() {
    const map = props.map;
    (visibleGeoBoundaryLayer && visibleGeoBoundaryLayer.length) && map.setLayoutProperty(visibleGeoBoundaryLayer, 'visibility', 'visible');
    setVisibleGeoBoundaryLayer('');
  }

  function flyTo(loc){
    props.map.flyTo({ center: [loc.lng, loc.lat], zoom: 12 });
    props.onMapUpdate && props.onMapUpdate();
  }

  function removeLocation(loc){
    session.crossfilterStore.remove_adhoc_point(loc);
    props.onMapUpdate && props.onMapUpdate();
  }

  return (
      <ExpandableMapControl onExpand={onExpand}
      openButton={<i className='fa-solid fa-map-pin adhoc-drag-pin' />}>
        <div className={classNames('map-adhoc', {'is-dragging': state.dragging})}>
          <header>
            <i className='fa-solid fa-map-pin' />
            <h3>Ad Hoc Locations</h3>
          </header>
          <div className='locations'>
            {session.crossfilterStore.adhoc_points.length === 0 &&
              <p><i>No Ad Hoc Locations</i></p> }
            {session.crossfilterStore.adhoc_points.length > 0 &&
                <ul>
                  { session.crossfilterStore.adhoc_points.map((loc, i)=>
                      <li key={i}>
                        <i className='map-pin fa-solid fa-map-pin' />
                        <span className='location-name'  title={`fly to ${loc.name}`}
                              onClick={flyTo.bind(null, loc)}>{loc.name}</span>
                        <i className='remove fa-solid fa-xmark' onClick={removeLocation.bind(null, loc)}
                            title={`remove ${loc.name}`} />
                      </li>) }
                </ul>}
          </div>
          <footer
              onClick={onClickPin}
              // onMouseDown={onMouseDownPin} onMouseUp={onMouseUpPin}
              >
            {state.dragging &&
            <>
              <i className='fa-solid fa-xmark' />
              <span>Cancel</span>
            </>
            }
            {!state.dragging &&
            <>
              <i className='fa-solid fa-plus'/>
              <span > Click to Add Ad Hoc Location</span>
            </>
            }
          </footer>
        </div>
      </ExpandableMapControl>
  );
}
